import React from "react";
import Image from "next/image";
import { Box, SimpleGrid } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { type Customer } from "lib/sanity";
import { Rule } from "ui/rule";
import { Statistic, TextTitle, type TextTitleProps, type StatisticProps } from "ui/text";
import { handleClientError } from "lib/errors";
import { Section, type SectionProps } from "components/section";
import { getCustomerLogoStyles, getCustomerLogoUrl } from "utils";
import { Link } from "ui/link";
import { trackCustomerLogosClicked } from "components/event-tracking";

/**
 * The set of available "featured customers" that we have hardcoded copy available to display
 * in the `CustomerStats` component.
 */
export enum CustomerWithStatsSlug {
  AUTO_TRADER = "auto-trader",
  GORGIAS = "gorgias",
  LUCID = "lucidchart",
  MINNO = "minno",
  PETSMART = "petsmart",
  WEIGHT_WATCHERS = "weight-watchers",
  WMG = "wmg",
  THE_ZEBRA = "the-zebra",
  RAMP = "ramp",
  CALENDLY = "calendly",
  HEALTHCARE_STAFFING_COMPANY = "healthcare-staffing-company",
  WHOOP = "whoop",
  WHOOP_AI_DECISIONING = "whoop-ai-decisioning",
  FUNDRISE = "fundrise",
}

export type CustomerStatsProps = {
  /**
   * The color theme to use for text and logo colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * Optional section title to display above the customer stats.
   */
  title?: TextTitleProps["title"];

  /**
   * The maximum width of the title.
   */
  titleMaxW?: TextTitleProps["maxW"];

  /**
   * The set of customer stats to display. Designed for displaying three stats.
   * See `CustomerWithStatsSlug` for the set of available customers.
   */
  customers: Array<Pick<Customer, "slug" | "name" | "logoUrl" | "logoMonoUrl">>;

  /**
   * Optional custom stats to display.
   * Use if the statistic is not related directly to an internal case study.
   */
  customStats?: StatProps[];
} & SectionProps;

/**
 * All stats are keyed by the `slug` value of the customer.
 */
const stats: Record<CustomerWithStatsSlug, Pick<StatisticProps, "title" | "description">> = {
  "auto-trader": {
    title: "20%",
    description: (
      <>
        increase
        <br />
        in CTR
      </>
    ),
  },
  gorgias: {
    title: "60%",
    description: (
      <>
        customer
        <br />
        acquisition lift
      </>
    ),
  },
  lucidchart: {
    title: "52%",
    description: (
      <>
        increase
        <br />
        in ROAS
      </>
    ),
  },
  minno: { title: "1 day", description: "migration" },
  petsmart: {
    title: "200%",
    description: (
      <>
        boost in
        <br />
        engagement
      </>
    ),
  },
  "weight-watchers": {
    title: "52%",
    description: (
      <>
        increase in new <br />
        customer acquisition
      </>
    ),
  },
  wmg: {
    title: "1000+",
    description: (
      <>
        audiences <br />
        managed
      </>
    ),
  },
  "the-zebra": {
    title: "170%",
    description: (
      <>
        match rate
        <br />
        increase
      </>
    ),
  },
  calendly: {
    title: "60%",
    description: (
      <>
        boost in <br />
        activation rate
      </>
    ),
  },
  ramp: {
    title: "25%",
    description: <>of all sales driven by data-powered outbound automation</>,
  },
  "healthcare-staffing-company": {
    title: "70%",
    description: (
      <>
        Lift in new <br />
        nurse retention
      </>
    ),
  },
  whoop: {
    title: "128%",
    description: <>Boost in Member engagement in fitness challenges</>,
  },
  "whoop-ai-decisioning": {
    title: "10%",
    description: (
      <>
        Lift in cross-sell <br />
        conversions
      </>
    ),
  },
  fundrise: {
    title: "4X",
    description: (
      <>
        Increase <br />
        in investments
      </>
    ),
  },
};

export function CustomerStats({
  theme = "light",
  title,
  titleMaxW,
  customers,
  customStats,
  ...sectionProps
}: CustomerStatsProps) {
  return (
    <Section my="huger" theme={theme} {...sectionProps}>
      {title && (
        <TextTitle
          align="center"
          mobileAlign="center"
          size="lg"
          title={title}
          maxW={titleMaxW}
          mb={{ base: "bigger", md: "huge" }}
        />
      )}

      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        alignItems="end"
        gap={{ base: "huge", md: "smaller", lg: "small" }}
        justifyItems="center"
      >
        {customStats?.map((stat) => (
          <Stat key={`${stat.customer.slug}-custom`} theme={theme} {...stat} />
        ))}

        {customers.map((customer, index) => {
          const stat = stats[customer.slug as CustomerWithStatsSlug];

          if (!stat) {
            handleClientError(`Could not find stat to display for customer with slug '${customer.slug}'.`);
          }

          return (
            <Stat
              // Index in key only needed to prevent potential rendering issues in Storybook
              // eslint-disable-next-line react/no-array-index-key
              key={`${customer.slug}-${index}`}
              theme={theme}
              customer={customer}
              {...stat}
            />
          );
        })}
      </SimpleGrid>
    </Section>
  );
}

type StatProps = {
  /**
   * Theme for the statistic.
   * @default "light"
   */
  theme?: Theme;

  /**
   * Optional href on the logo. If not provided, the logo will link to the case study page for the customer.
   * @default `/customers/${customer.slug}`
   */
  href?: string;

  /**
   * The customer to display the statistic for.
   */
  customer: Pick<Customer, "slug" | "name" | "logoUrl" | "logoMonoUrl">;
} & Pick<StatisticProps, "title" | "description">;

function Stat({ theme = "light", href, title, description, customer }: StatProps) {
  return (
    <Box
      w="100%"
      maxW={{
        base: "100%",
        sm: "350px", // Only cap width from sm to md breakpoint so the single column doesn't look as silly there
        md: "100%",
      }}
    >
      <Statistic theme={theme} title={title} description={description} />
      <Rule theme={theme} pb="smaller" />
      <Box w="170px" h="30px">
        <Link
          variant="unstyled"
          aria-label={`Read ${customer.name} customer story.`}
          href={href ?? `/customers/${customer.slug}`}
          target={href ? "_blank" : undefined}
          {...getCustomerLogoStyles(theme)}
          onClick={() => {
            trackCustomerLogosClicked("customer-stats", customer.slug);
          }}
        >
          <Image
            sizes="100%"
            width="170"
            height="30"
            src={`${getCustomerLogoUrl(customer, theme)}?w=400`}
            alt={`${customer.name} logo.`}
            style={{
              height: "30px",
              width: "auto",
              objectFit: "contain",
            }}
          />
        </Link>
      </Box>
    </Box>
  );
}
